import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Mail, Phone, MapPin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { POLICY_LINKS, isHidden } from "@/lib/siteNav";

const QUICK_LINKS = [
  { id: "pil", label: "PILs", to: "/pil" },
  { id: "media", label: "Media", to: "/media" },
  { id: "documents", label: "Documents", to: "/documents" },
  { id: "about", label: "About", to: "/about" },
  { id: "contact", label: "Contact", to: "/contact" },
  { id: "donate", label: "Support Us", to: "/donate" },
];

const linkCls = "group inline-flex items-center gap-2 hover:text-white transition-colors";
const Dot = () => <span className="h-px w-0 bg-npst-gold transition-all duration-300 group-hover:w-3" />;

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" data-testid="back-to-top-btn"
      className={`fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-npst-goldBorder bg-white text-npst-navy shadow-lg transition-[opacity,transform] duration-300 hover:bg-npst-navy hover:text-white ${show ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-3"}`}>
      <ArrowUp size={18} />
    </button>
  );
}

export default function Footer() {
  const s = useSettings();
  const year = new Date().getFullYear();
  const hidden = (id) => isHidden(s, id);
  const quick = QUICK_LINKS.filter((l) => !hidden(l.id));
  const policies = POLICY_LINKS.filter((l) => !hidden(l.id));

  return (
    <footer className="relative bg-npst-navyDeep text-slate-300 mt-24" data-testid="site-footer">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-npst-gold/70 to-transparent" aria-hidden="true" />
      <BackToTop />
      <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-10">
        <div className="sm:col-span-2 md:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <img src={s.logo || "/npst-logo.webp"} alt="NPST emblem" className="w-12 h-12 object-contain bg-white rounded-sm p-1" />
            <div className="font-display text-base font-bold tracking-wide text-white">NATIONAL PUBLIC SERVICE TRUST</div>
          </div>
          {!hidden("footer-intro") && (
            <p className="text-sm leading-relaxed text-slate-400 max-w-md">{s.intro?.slice(0, 220) || "A public-interest institution advancing constitutional values, the rule of law and administrative transparency."}</p>
          )}
        </div>
        {!hidden("footer-quicklinks") && quick.length > 0 && (
          <div className="md:col-span-2" data-testid="footer-quicklinks">
            <div className="label-eyebrow text-npst-gold mb-4">Quick Links</div>
            <ul className="space-y-2.5 text-sm">
              {quick.map((l) => <li key={l.id}><Link to={l.to} className={linkCls}><Dot />{l.label}</Link></li>)}
            </ul>
          </div>
        )}
        {!hidden("footer-policies") && policies.length > 0 && (
          <div className="md:col-span-2" data-testid="footer-policies">
            <div className="label-eyebrow text-npst-gold mb-4">Policies</div>
            <ul className="space-y-2.5 text-sm">
              {policies.map((l) => <li key={l.id}><Link to={l.to} className={linkCls}><Dot />{l.label}</Link></li>)}
              <li><Link to="/verify-donation" className={linkCls} data-testid="footer-verify-receipt"><Dot />Verify a Receipt</Link></li>
              <li><Link to="/receipts" className={linkCls} data-testid="footer-donor-receipts"><Dot />Download My Receipt</Link></li>
            </ul>
          </div>
        )}
        {!hidden("footer-contact") && (s.address || s.email || s.phone) && (
          <div className="sm:col-span-2 md:col-span-3" data-testid="footer-contact">
            <div className="label-eyebrow text-npst-gold mb-4">Contact</div>
            <ul className="space-y-3 text-sm text-slate-400">
              {s.address && <li className="flex gap-2.5"><MapPin size={15} className="mt-0.5 shrink-0 text-npst-gold" /><span>{s.address}</span></li>}
              {s.email && <li className="flex gap-2.5"><Mail size={15} className="mt-0.5 shrink-0 text-npst-gold" /><a href={`mailto:${s.email}`} className="break-all hover:text-white transition-colors">{s.email}</a></li>}
              {s.phone && <li className="flex gap-2.5"><Phone size={15} className="mt-0.5 shrink-0 text-npst-gold" /><a href={`tel:${s.phone}`} className="hover:text-white transition-colors">{s.phone}</a></li>}
            </ul>
          </div>
        )}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500 text-center">
          <span>© {year} {s.org_name || "National Public Service Trust"}. All rights reserved.</span>
          {s.registration_number && <span className="font-mono">Reg. {s.registration_number}</span>}
        </div>
      </div>
    </footer>
  );
}
